import { SyntheticEvent } from 'react';
import redirect from 'lib/redirect';
import { getLocale } from 'lib/i18n';
import { deferred } from 'lib/promise';
import { sanitizePath } from 'lib/metrics';
import { Country, HomeId } from 'types';
import { PlanType } from 'lib/plans';
import { logTrackingError } from 'lib/semanticMetrics';
import {
  getEventSender,
  EventData,
  EventSenderLogOptions,
} from 'services/eventSender';
import { isRunningInCypress } from 'lib/env';

const RESOLVE_TIMEOUT = 20000;
const FLUSH_TIMEOUT = 800;
const FLUSH_TIMEOUT_IN_TESTS = 20000;

export type UserType = 'master' | 'sub' | '';

export const country = deferred<Country>();
export const setCountry = (c: Country) => country.resolve(c);

export const userType = deferred<UserType>();
export const setUserType = (u: UserType) => userType.resolve(u);

export const planType = deferred<PlanType>();
export const setPlanType = (p: PlanType) => planType.resolve(p);

export const homeId = deferred<HomeId | null>();
export const setHomeId = (h: HomeId | null) => homeId.resolve(h);

const resolveParamsAndTrack = async (
  trackingEvent: Promise<EventData>,
  senderLogOptions: EventSenderLogOptions,
) => {
  const eventSender = await getEventSender();

  const trackingEventPayload = await trackingEvent;

  if (typeof window.__DNF_EVENT_LOGGER__ === 'object') {
    try {
      window.__DNF_EVENT_LOGGER__.push(trackingEventPayload);
    } catch (e) {
      /**/
    }
  }
  return eventSender.send(trackingEventPayload, senderLogOptions);
};

export type TrackingEvent = Promise<EventData>;

const getTrackingTimeout = (timeout: number): Promise<void> =>
  new Promise((_, r) =>
    setTimeout(
      () => r(new Error(`Tracking timed out in ${timeout} ms.`)),
      timeout,
    ),
  );

export const trackEvent = (
  trackingEvent: TrackingEvent,
  senderOptions: EventSenderLogOptions = {},
  timeout = RESOLVE_TIMEOUT,
) =>
  Promise.race([
    resolveParamsAndTrack(trackingEvent, senderOptions),
    getTrackingTimeout(timeout),
  ]).catch(e => {
    logTrackingError(
      e.message,
      sanitizePath(window.location.pathname),
      getLocale(),
    );
  });

const getClosestLink = (node: any) => {
  let current = node;
  while (current && current !== document) {
    if (current && current.hasAttribute('href')) {
      return current;
    }
    current = current.parentNode;
  }
  return node;
};

const getEventFlushTimeout = (timeout: number) => {
  return new Promise(resolve => setTimeout(resolve, timeout));
};

export const trackAndRedirect = async (
  trackingEvents: TrackingEvent | TrackingEvent[],
  path: string,
  senderOptions: EventSenderLogOptions = {},
) => {
  try {
    if (Array.isArray(trackingEvents)) {
      for (const trackingEvent of trackingEvents) {
        await trackEvent(trackingEvent, { flush: false, ...senderOptions });
      }
    } else {
      await trackEvent(trackingEvents, { flush: false, ...senderOptions });
    }
    const eventSender = await getEventSender();
    await Promise.race([
      getEventFlushTimeout(
        isRunningInCypress() ? FLUSH_TIMEOUT_IN_TESTS : FLUSH_TIMEOUT,
      ),
      eventSender.flush(),
    ]);
  } finally {
    redirect(path);
  }
};

export const trackLinkEvent = async (
  domEvent: SyntheticEvent | Event,
  trackingEvent: TrackingEvent,
  senderOptions: EventSenderLogOptions = {},
) => {
  domEvent.preventDefault();
  const path = getClosestLink(domEvent.target).getAttribute('href');
  await trackAndRedirect(trackingEvent, path, senderOptions);
};

export const withTrackLinkEvent =
  (trackingEvent: TrackingEvent, senderOptions: EventSenderLogOptions = {}) =>
  (domEvent: any) =>
    trackLinkEvent(domEvent, trackingEvent, senderOptions);

export const withTrackEvent =
  (
    trackingEvent: TrackingEvent,
    handler: any,
    senderOptions: EventSenderLogOptions = {},
  ) =>
  (...params: any[]) => {
    const trackingPromise = trackEvent(trackingEvent, senderOptions);
    handler(...params);
    return trackingPromise;
  };

export const getTrackWithRedirectHandler =
  (
    trackingEvent: TrackingEvent,
    path: string,
    senderOptions: EventSenderLogOptions = {},
  ) =>
  () =>
    trackAndRedirect(trackingEvent, path, senderOptions);
